import {
    AlignCenterOutlined,
    AlignLeftOutlined,
    AlignRightOutlined,
    BgColorsOutlined,
    BoldOutlined,
    ItalicOutlined,
    LinkOutlined,
    OrderedListOutlined,
    PictureOutlined,
    RedoOutlined,
    SmileOutlined,
    StrikethroughOutlined,
    UnderlineOutlined,
    UndoOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import Color from '@tiptap/extension-color';
import {
    mergeAttributes,
    ResizableNodeView,
} from '@tiptap/core';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import {
    EditorContent,
    useEditor,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
    ConfigProvider,
    theme as AntdTheme,
} from 'antd';
import * as Antd from 'antd';
import {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useUIStore } from '@/stores';
import { lightTheme } from '@/themes';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        fontSize: {
            setFontSize: (size: string) => ReturnType;
            unsetFontSize: () => ReturnType;
        };
    }
}

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96];

const EditorContentWrapper = ({ editor, linkModalOpen, linkUrl, setLinkModalOpen, setLinkUrl, editorMinHeightPx }: {
    editor: any;
    linkModalOpen: boolean;
    linkUrl: string;
    setLinkModalOpen: (open: boolean) => void;
    setLinkUrl: (url: string) => void;
    editorMinHeightPx: number;
}) => {
    const { theme } = useUIStore();

    const lightThemeToken = useMemo(() => {
        return AntdTheme.getDesignToken({
            algorithm: AntdTheme.defaultAlgorithm,
            ...lightTheme,
        });
    }, []);

    // TODO
    const backgroundColor = theme === 'dark'
        ? '#ADADAD'
        : lightThemeToken.colorFillTertiary;

    return (
        <ConfigProvider
            theme={{
                algorithm: AntdTheme.defaultAlgorithm,
                ...lightTheme,
            }}
        >
            <div style={{
                padding: '12px',
                minHeight: `${editorMinHeightPx}px`,
                backgroundColor,
            }}>
                <style>
                    {`
                        .ProseMirror {
                            outline: none !important;
                            height: ${editorMinHeightPx}px;
                            color: ${lightThemeToken.colorText} !important;
                            overflow-y: auto;
                        }
                        .ProseMirror:focus {
                            outline: none !important;
                        }
                        .ProseMirror p,
                        .ProseMirror h1,
                        .ProseMirror h2,
                        .ProseMirror h3,
                        .ProseMirror h4,
                        .ProseMirror h5,
                        .ProseMirror h6,
                        .ProseMirror ul,
                        .ProseMirror ol,
                        .ProseMirror li,
                        .ProseMirror blockquote {
                            color: ${lightThemeToken.colorText} !important;
                        }
                        .ProseMirror a {
                            color: ${lightThemeToken.colorLink} !important;
                        }
                        .ProseMirror code {
                            color: ${lightThemeToken.colorText} !important;
                            background-color: ${lightThemeToken.colorFillTertiary} !important;
                        }
                        .ProseMirror img {
                            display: block;
                            max-width: 100%;
                            height: auto;
                            cursor: nwse-resize;
                        }
                        .ProseMirror img.ProseMirror-selectednode {
                            outline: 2px solid ${lightThemeToken.colorPrimary};
                            outline-offset: 2px;
                            border-radius: 6px;
                        }
                        .ProseMirror [data-resize-wrapper] {
                            line-height: 0;
                            vertical-align: top;
                        }
                        .ProseMirror [data-resize-container] {
                            width: 100%;
                        }
                        .ProseMirror [data-resize-handle] {
                            position: absolute;
                            width: 12px;
                            height: 12px;
                            border-radius: 999px;
                            background: ${lightThemeToken.colorPrimary};
                            border: 2px solid #fff;
                            box-shadow: 0 0 0 1px ${lightThemeToken.colorPrimary};
                            z-index: 2;
                            opacity: 0;
                            pointer-events: none;
                            transition: opacity 0.15s ease;
                        }
                        .ProseMirror [data-resize-wrapper]:hover [data-resize-handle] {
                            opacity: 1;
                            pointer-events: auto;
                        }
                        .ProseMirror [data-resize-wrapper]:has(> img.ProseMirror-selectednode) [data-resize-handle] {
                            opacity: 1;
                            pointer-events: auto;
                        }
                        .ProseMirror [data-resize-container][data-resize-state="true"] [data-resize-handle] {
                            opacity: 1;
                            pointer-events: auto;
                        }
                        .ProseMirror [data-resize-handle="top-left"] {
                            top: 0;
                            left: 0;
                            transform: translate(0, 0);
                            cursor: nwse-resize;
                        }
                        .ProseMirror [data-resize-handle="top-right"] {
                            top: 0;
                            right: 0;
                            transform: translate(0, 0);
                            cursor: nesw-resize;
                        }
                        .ProseMirror [data-resize-handle="bottom-left"] {
                            bottom: 0;
                            left: 0;
                            transform: translate(0, 0);
                            cursor: nesw-resize;
                        }
                        .ProseMirror [data-resize-handle="bottom-right"] {
                            bottom: 0;
                            right: 0;
                            transform: translate(0, 0);
                            cursor: nwse-resize;
                        }
                    `}
                </style>
                <EditorContent editor={editor} />

                <Antd.Modal
                    title="Додати/Редагувати посилання"
                    open={linkModalOpen}
                    onOk={() => {
                        if (linkUrl) {
                            editor.chain().focus().setLink({ href: linkUrl }).run();
                        } else {
                            editor.chain().focus().unsetLink().run();
                        }
                        setLinkModalOpen(false);
                        setLinkUrl('');
                    }}
                    onCancel={() => {
                        setLinkModalOpen(false);
                        setLinkUrl('');
                    }}
                >
                    <Antd.Input
                        placeholder="Введіть URL"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        onPressEnter={() => {
                            if (linkUrl) {
                                editor.chain().focus().setLink({ href: linkUrl }).run();
                            } else {
                                editor.chain().focus().unsetLink().run();
                            }
                            setLinkModalOpen(false);
                            setLinkUrl('');
                        }}
                    />
                </Antd.Modal>
            </div>
        </ConfigProvider>
    );
};

type TextEditorProps = {
    value?: string;
    onChange?: (value: string) => void;
    defaultValue?: string;
    disabled?: boolean;
    onImageUpload?: (file: File) => Promise<{ url: string }>;
    /** Мінімальна висота області редагування (px). За замовчуванням 200. */
    editorMinHeightPx?: number;
};

const DEFAULT_EDITOR_MIN_HEIGHT_PX = 200;
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export const TextEditor = ({
    value,
    onChange,
    defaultValue,
    disabled = false,
    onImageUpload,
    editorMinHeightPx = DEFAULT_EDITOR_MIN_HEIGHT_PX,
}: TextEditorProps) => {
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [isImageUploading, setIsImageUploading] = useState(false);
    const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const lastSelectedImagePosRef = useRef<number | null>(null);

    const FontSize = TextStyle.extend({
        addAttributes() {
            return {
                fontSize: {
                    default: null,
                    parseHTML: (element: HTMLElement) => {
                        const fontSize = element.style.fontSize;
                        return fontSize ? fontSize.replace('px', '') : null;
                    },
                    renderHTML: (attributes: { fontSize?: string | null }) => {
                        if (!attributes.fontSize) {
                            return {};
                        }
                        return {
                            style: `font-size: ${attributes.fontSize}px`,
                        };
                    },
                },
            };
        },

        addCommands() {
            return {
                setFontSize:
                (fontSize: string) =>
                    ({ chain }: { chain: () => any }) => {
                        return chain().setMark('textStyle', { fontSize }).run();
                    },
                unsetFontSize:
                () =>
                    ({ chain }: { chain: () => any }) => {
                        return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
                    },
            };
        },
    });

    const CustomImage = Image.extend({
        addAttributes() {
            return {
                ...(this as any).parent?.(),
                align: {
                    default: 'left',
                    parseHTML: (element: HTMLElement) => element.getAttribute('data-align') || 'left',
                    renderHTML: (attributes: { align?: string | null }) => ({
                        'data-align': attributes.align || 'left',
                    }),
                },
            };
        },
        renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, any> }) {
            return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
        },
        addNodeView() {
            if (
                !this.options.resize
                || !this.options.resize.enabled
                || typeof document === 'undefined'
                || !this.editor.isEditable
            ) {
                return null;
            }

            const {
                directions, minWidth, minHeight, alwaysPreserveAspectRatio,
            } = this.options.resize;

            return ({ node, getPos, HTMLAttributes }: {
                node: any;
                getPos: () => number | undefined;
                HTMLAttributes: Record<string, any>;
            }) => {
                const el = document.createElement('img');

                Object.entries(HTMLAttributes).forEach(([key, value]) => {
                    if (value != null && key !== 'width' && key !== 'height') {
                        el.setAttribute(key, String(value));
                    }
                });
                el.src = HTMLAttributes.src;

                const nodeView = new ResizableNodeView({
                    element: el,
                    node,
                    editor: this.editor,
                    getPos,
                    onResize: (width: number, height: number) => {
                        el.style.width = `${width}px`;
                        el.style.height = `${height}px`;
                    },
                    onCommit: (width: number, height: number) => {
                        const pos = getPos();
                        if (pos === undefined) return;

                        this.editor
                            .chain()
                            .setNodeSelection(pos)
                            .updateAttributes(this.name, { width, height })
                            .run();
                    },
                    onUpdate: (updatedNode: any) => {
                        if (updatedNode.type !== node.type) {
                            return false;
                        }

                        const align = updatedNode.attrs?.align || 'left';
                        const dom = nodeView.dom as HTMLElement;
                        dom.style.display = 'flex';
                        dom.style.justifyContent = align === 'center'
                            ? 'center'
                            : align === 'right'
                                ? 'flex-end'
                                : 'flex-start';

                        return true;
                    },
                    options: {
                        directions,
                        min: {
                            width: minWidth,
                            height: minHeight,
                        },
                        preserveAspectRatio: alwaysPreserveAspectRatio === true,
                    },
                });

                const dom = nodeView.dom as HTMLElement;
                const align = node.attrs?.align || 'left';
                dom.style.width = '100%';
                dom.style.display = 'flex';
                dom.style.justifyContent = align === 'center'
                    ? 'center'
                    : align === 'right'
                        ? 'flex-end'
                        : 'flex-start';

                const wrapper = dom.querySelector('[data-resize-wrapper]') as HTMLElement | null;
                if (wrapper) {
                    wrapper.style.display = 'inline-block';
                    wrapper.style.width = 'auto';
                    wrapper.style.maxWidth = '100%';
                }

                dom.style.visibility = 'hidden';
                dom.style.pointerEvents = 'none';
                el.onload = () => {
                    dom.style.visibility = '';
                    dom.style.pointerEvents = '';
                };

                return nodeView;
            };
        },
    });

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                link: false,
                underline: false,
            }),
            Underline,
            FontSize,
            Color,
            CustomImage.configure({
                resize: {
                    enabled: true,
                    minWidth: 80,
                    alwaysPreserveAspectRatio: true,
                },
                HTMLAttributes: {
                    style: 'max-width: 100%; height: auto;',
                },
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Link.configure({
                openOnClick: false,
            }),
        ],
        content: value || defaultValue,
        onUpdate: ({ editor }) => {
            onChange?.(editor.getHTML());
        },
        editorProps: {
            transformPastedHTML: (html: string) => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');

                // TODO
                // remove images after copy content to field
                const images = doc.querySelectorAll('img');
                images.forEach((img) => img.remove());

                const elements = doc.querySelectorAll('*');
                elements.forEach((element) => {
                    const htmlElement = element as HTMLElement;
                    htmlElement.removeAttribute('style');
                });

                return doc.body.innerHTML;
            },
        },
    });

    useEffect(() => {
        if (editor && value !== undefined && editor.getHTML() !== value) {
            editor.commands.setContent(value);
        }
    }, [value, editor]);

    useEffect(() => {
        if (editor) {
            editor.setEditable(!disabled);
        }
    }, [editor, disabled]);

    useEffect(() => {
        if (!editor) {
            return;
        }

        const updateLastSelectedImagePos = () => {
            const selection = editor.state.selection as any;
            const selectedNode = selection?.node;
            if (selectedNode?.type?.name === 'image') {
                lastSelectedImagePosRef.current = selection.from;
            }
        };

        editor.on('selectionUpdate', updateLastSelectedImagePos);
        updateLastSelectedImagePos();

        return () => {
            editor.off('selectionUpdate', updateLastSelectedImagePos);
        };
    }, [editor]);

    const validateImage = (file: File): boolean => {
        if (!ALLOWED_IMAGE_MIME_TYPES.includes(file.type)) {
            Antd.message.error('Підтримуються лише jpeg, png, webp або gif');
            return false;
        }

        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            Antd.message.error('Максимальний розмір зображення 10MB');
            return false;
        }

        return true;
    };

    const handleImageUpload = async (file: File) => {
        if (!editor || !onImageUpload) {
            return;
        }

        if (!validateImage(file)) {
            return;
        }

        setIsImageUploading(true);

        try {
            const uploadedImage = await onImageUpload(file);

            if (!uploadedImage?.url) {
                Antd.message.error('Не вдалося отримати адресу зображення');
                return;
            }
            const chain = editor.chain().focus().setImage({ src: uploadedImage.url });
            const result = chain.run();
        } catch(err) {
            console.error(err)
            Antd.message.error('Не вдалося вставити зображення');
        } finally {
            setIsImageUploading(false);
        }
    };

    const handleImageInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        await handleImageUpload(file);
        event.target.value = '';
    };

    const applyAlign = (align: 'left' | 'center' | 'right') => {
        if (!editor) {
            return;
        }

        const selection = editor.state.selection as any;
        const selectedNode = selection?.node;
        const imagePos = selectedNode?.type?.name === 'image'
            ? selection.from
            : lastSelectedImagePosRef.current;

        if (imagePos !== null) {
            editor.chain()
                .focus()
                .command(({ tr }) => {
                    const node = tr.doc.nodeAt(imagePos);
                    if (!node || node.type.name !== 'image') {
                        return false;
                    }

                    tr.setNodeMarkup(imagePos, undefined, {
                        ...node.attrs,
                        align,
                    });

                    return true;
                })
                .setNodeSelection(imagePos)
                .run();

            lastSelectedImagePosRef.current = imagePos;
            return;
        }

        editor
                .chain()
                .focus()
                .setTextAlign(align)
                .run();
    };

    return (
        <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', overflow: 'hidden' }}>
            {editor && (
                <div style={{
                    borderBottom: '1px solid #d9d9d9',
                    padding: '8px',
                    display: 'flex',
                    gap: '4px',
                    flexWrap: 'wrap',
                }}
                >
                    <Antd.Button
                        size="small"
                        icon={<BoldOutlined />}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        type={editor.isActive('bold') ? 'primary' : 'default'}
                        disabled={disabled}
                    />
                    <Antd.Button
                        size="small"
                        icon={<ItalicOutlined />}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        type={editor.isActive('italic') ? 'primary' : 'default'}
                    />
                    <Antd.Button
                        size="small"
                        icon={<UnderlineOutlined />}
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        type={editor.isActive('underline') ? 'primary' : 'default'}
                    />
                    <Antd.Button
                        size="small"
                        icon={<StrikethroughOutlined />}
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        type={editor.isActive('strike') ? 'primary' : 'default'}
                    />

                    <Antd.Divider type="vertical" style={{ margin: '0 4px' }} />

                    <Antd.Select
                        size="small"
                        placeholder="Розмір"
                        defaultValue="14"
                        style={{ width: 80 }}
                        onChange={(value) => {
                            editor.chain().focus().setFontSize(value).run();
                        }}
                        options={FONT_SIZES.map((size) => ({
                            value: String(size),
                            label: String(size),
                        }))}
                        disabled={disabled}
                    />

                    <Antd.Divider type="vertical" style={{ margin: '0 4px' }} />

                    <Antd.Button
                        size="small"
                        icon={<UnorderedListOutlined />}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        type={editor.isActive('bulletList') ? 'primary' : 'default'}
                    />
                    <Antd.Button
                        size="small"
                        icon={<OrderedListOutlined />}
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        type={editor.isActive('orderedList') ? 'primary' : 'default'}
                    />

                    <Antd.Divider type="vertical" style={{ margin: '0 4px' }} />

                    <Antd.Button
                        size="small"
                        icon={<AlignLeftOutlined />}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => applyAlign('left')}
                        type={editor.isActive('image', { align: 'left' }) || editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
                    />
                    <Antd.Button
                        size="small"
                        icon={<AlignCenterOutlined />}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => applyAlign('center')}
                        type={editor.isActive('image', { align: 'center' }) || editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
                    />
                    <Antd.Button
                        size="small"
                        icon={<AlignRightOutlined />}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => applyAlign('right')}
                        type={editor.isActive('image', { align: 'right' }) || editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
                    />

                    <Antd.Divider type="vertical" style={{ margin: '0 4px' }} />

                    <Antd.Button
                        size="small"
                        icon={<LinkOutlined />}
                        onClick={() => {
                            const currentLink = editor.getAttributes('link').href || '';
                            setLinkUrl(currentLink);
                            setLinkModalOpen(true);
                        }}
                        type={editor.isActive('link') ? 'primary' : 'default'}
                    />
                    {onImageUpload && (
                        <>
                            <Antd.Button
                                size="small"
                                icon={<PictureOutlined />}
                                onClick={() => imageInputRef.current?.click()}
                                disabled={disabled}
                                loading={isImageUploading}
                            />
                            <input
                                ref={imageInputRef}
                                type="file"
                                accept=".jpg,.jpeg,.png,.webp,.gif"
                                style={{ display: 'none' }}
                                onChange={(event) => {
                                    void handleImageInputChange(event);
                                }}
                            />
                        </>
                    )}

                    <Antd.ColorPicker
                        size="small"
                        disabledAlpha
                        showText
                        defaultValue="#000000"
                        onChange={(color) => {
                            editor.chain().focus().setColor(color.toHexString()).run();
                        }}
                        disabled={disabled}
                    >
                        <Antd.Button
                            size="small"
                            icon={<BgColorsOutlined />}
                            disabled={disabled}
                        />
                    </Antd.ColorPicker>

                    <Antd.Divider type="vertical" style={{ margin: '0 4px' }} />

                    <Antd.Button
                        size="small"
                        icon={<UndoOutlined />}
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().undo()}
                    />
                    <Antd.Button
                        size="small"
                        icon={<RedoOutlined />}
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().redo()}
                    />

                    <Antd.Divider type="vertical" style={{ margin: '0 4px' }} />

                    <Antd.Popover
                        open={emojiPickerOpen}
                        onOpenChange={(open) => {
                            if (!disabled) setEmojiPickerOpen(open);
                        }}
                        trigger="click"
                        content={
                            <Picker
                                data={data}
                                locale="uk"
                                onEmojiSelect={(emoji: { native: string }) => {
                                    editor.chain().focus().insertContent(emoji.native).run();
                                    setEmojiPickerOpen(false);
                                }}
                                previewPosition="none"
                                skinTonePosition="none"
                            />
                        }
                        overlayInnerStyle={{ padding: 0 }}
                    >
                        <Antd.Button
                            size="small"
                            icon={<SmileOutlined />}
                            disabled={disabled}
                            type={emojiPickerOpen ? 'primary' : 'default'}
                        />
                    </Antd.Popover>
                </div>
            )}
            <EditorContentWrapper
                editor={editor}
                linkModalOpen={linkModalOpen}
                linkUrl={linkUrl}
                setLinkModalOpen={setLinkModalOpen}
                setLinkUrl={setLinkUrl}
                editorMinHeightPx={editorMinHeightPx}
            />
        </div>
    );
};
